import {
  Anchor,
  Award,
  BadgeCheck,
  Bike,
  Briefcase,
  Bus,
  Calendar,
  Camera,
  Car,
  CircleCheck,
  Clock,
  Cloud,
  CloudRain,
  CloudSun,
  Compass,
  CreditCard,
  Crown,
  Droplets,
  Flame,
  Footprints,
  Gem,
  Gift,
  Globe,
  Headphones,
  Heart,
  HeartHandshake,
  Hotel,
  Leaf,
  Luggage,
  Map,
  MapPin,
  Medal,
  Mountain,
  Percent,
  Phone,
  Plane,
  Receipt,
  Route,
  Ship,
  Shirt,
  Shield,
  Plug,
  ShieldCheck,
  ShoppingBag,
  Smile,
  Sparkles,
  Star,
  Sun,
  Tent,
  Thermometer,
  ThumbsUp,
  Ticket,
  TrendingUp,
  Trophy,
  Umbrella,
  Users,
  Wallet,
  Waves,
} from 'lucide-react';
import {
  FaFacebookF,
  FaGoogle,
  FaInstagram,
  FaLinkedinIn,
  FaPinterestP,
  FaStar,
  FaTelegram,
  FaTiktok,
  FaWhatsapp,
  FaXTwitter,
  FaYoutube,
} from 'react-icons/fa6';

import type { ComponentType } from 'react';

/**
 * Curated icon registry shared by the admin (CMS icon picker) and the website
 * (renders the chosen icon). Keys are kebab-case names stored in section data.
 *
 * Mostly lucide-react; social/brand glyphs come from react-icons (lucide
 * dropped brand icons). Both render as components that accept `className`, so
 * we type the map by that common shape.
 */
export type IconComponent = ComponentType<{ className?: string }>;

/** General-purpose icons (lucide). */
export const GENERAL_ICONS: Record<string, IconComponent> = {
  'trending-up': TrendingUp,
  star: Star,
  award: Award,
  trophy: Trophy,
  medal: Medal,
  'badge-check': BadgeCheck,
  'circle-check': CircleCheck,
  shield: Shield,
  'shield-check': ShieldCheck,
  'thumbs-up': ThumbsUp,
  heart: Heart,
  'heart-handshake': HeartHandshake,
  smile: Smile,
  sparkles: Sparkles,
  gem: Gem,
  crown: Crown,
  globe: Globe,
  users: Users,
  plane: Plane,
  'map-pin': MapPin,
  map: Map,
  compass: Compass,
  route: Route,
  mountain: Mountain,
  tent: Tent,
  sun: Sun,
  // Weather set - added for the destination guides' climate tiles, but
  // available to every icon field like the rest of the registry.
  cloud: Cloud,
  'cloud-sun': CloudSun,
  'cloud-rain': CloudRain,
  droplets: Droplets,
  thermometer: Thermometer,
  umbrella: Umbrella,
  waves: Waves,
  leaf: Leaf,
  flame: Flame,
  car: Car,
  bus: Bus,
  bike: Bike,
  ship: Ship,
  anchor: Anchor,
  hotel: Hotel,
  luggage: Luggage,
  // Packing-list set - added for the guides' packing pills, available anywhere.
  shirt: Shirt,
  footprints: Footprints,
  plug: Plug,
  briefcase: Briefcase,
  camera: Camera,
  ticket: Ticket,
  calendar: Calendar,
  clock: Clock,
  phone: Phone,
  headphones: Headphones,
  wallet: Wallet,
  'credit-card': CreditCard,
  receipt: Receipt,
  percent: Percent,
  gift: Gift,
  // Added for the guides' travel-style tiles (shopping), like the weather set
  // above - available to every icon field, not just that section.
  'shopping-bag': ShoppingBag,
};

/** Social / brand icons (react-icons). Keys match social platform slugs. */
export const SOCIAL_ICONS: Record<string, IconComponent> = {
  facebook: FaFacebookF,
  instagram: FaInstagram,
  linkedin: FaLinkedinIn,
  youtube: FaYoutube,
  x: FaXTwitter,
  twitter: FaXTwitter,
  whatsapp: FaWhatsapp,
  telegram: FaTelegram,
  pinterest: FaPinterestP,
  tiktok: FaTiktok,
  google: FaGoogle,
  'google-review': FaGoogle,
  trustpilot: FaStar,
};

export const ICON_REGISTRY: Record<string, IconComponent> = {
  ...GENERAL_ICONS,
  ...SOCIAL_ICONS,
};

/** Sorted list of registry icon names (for pickers). */
export const ICON_NAMES: string[] = Object.keys(ICON_REGISTRY);

/** Resolve a stored icon name to its component, or undefined if unknown. */
export function getIcon(name?: string | null): IconComponent | undefined {
  return name ? ICON_REGISTRY[name] : undefined;
}
